import { useId, useRef } from "react";
import { SUBJECT_AREA_PALETTE } from "../lib/subject-area-palette";

interface SubjectAreaColorPickerProps {
  colorHex: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
  /**
   * Included in generated `data-testid` values so callers can distinguish
   * between the "row" instance in EditSubjectAreasDialog and the "form"
   * instance in SubjectAreaFormDialog.
   */
  testIdPrefix: string;
  size?: "sm" | "md";
}

/**
 * Palette picker + native color input in one row. The six curated swatches
 * cover the common case and stay visually consistent across schedules; the
 * trailing "Custom" tile opens the OS color picker (via a hidden
 * `<input type="color">`) so admins can dial in any hex value. When the
 * current color matches a palette swatch, that swatch is ringed. Otherwise
 * the Custom tile is ringed and shows the actual color, so admins can tell
 * at a glance that a bespoke color is in use.
 */
export function SubjectAreaColorPicker({
  colorHex,
  onChange,
  disabled = false,
  ariaLabel = "Color",
  testIdPrefix,
  size = "md",
}: SubjectAreaColorPickerProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const normalized = colorHex.toUpperCase();
  const isCustom = !SUBJECT_AREA_PALETTE.some(
    (entry) => entry.hex.toUpperCase() === normalized,
  );

  const sizeClass = size === "sm" ? "w-6 h-6" : "w-7 h-7";

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="flex gap-1.5 items-center"
    >
      {SUBJECT_AREA_PALETTE.map((entry) => {
        const selected = entry.hex.toUpperCase() === normalized;
        return (
          <button
            key={entry.hex}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={entry.name}
            disabled={disabled}
            onClick={() => onChange(entry.hex)}
            data-testid={`${testIdPrefix}-${entry.hex}`}
            className={`${sizeClass} rounded-full cursor-pointer disabled:cursor-not-allowed ring-2 ring-offset-2 ring-offset-surface ${
              selected ? "ring-white" : "ring-transparent"
            } focus:outline-none focus-visible:ring-brand`}
            style={{ backgroundColor: entry.hex }}
          />
        );
      })}

      {/* Custom color tile. Clicking it opens the OS color picker. When a
          custom color is in use we show it directly as the tile's fill; when
          a palette entry is selected we show a rainbow gradient so it reads
          as "open custom picker". */}
      <label
        htmlFor={inputId}
        aria-label="Custom color"
        data-testid={`${testIdPrefix}-custom`}
        aria-checked={isCustom}
        role="radio"
        className={`${sizeClass} rounded-full cursor-pointer ring-2 ring-offset-2 ring-offset-surface ${
          isCustom ? "ring-white" : "ring-transparent"
        } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        style={
          isCustom
            ? { backgroundColor: colorHex }
            : {
                background:
                  "conic-gradient(from 0deg, #ef4444, #f59e0b, #eab308, #22c55e, #06b6d4, #3b82f6, #8b5cf6, #ef4444)",
              }
        }
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        tabIndex={disabled ? -1 : 0}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="color"
          value={colorHex}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="sr-only"
          data-testid={`${testIdPrefix}-custom-input`}
        />
      </label>
    </div>
  );
}
